import { useCallback, useEffect, useRef } from 'react';
import { LocationData, Upgrades, StaffMember, BackboneLink, AvailableContract, ActiveContract, Loan, PendingDelivery } from '../types';
import { ActiveEvent } from './useRandomEvents';

export const SAVE_VERSION = 14; // v14: 14 new world locations (Americas/MEA/APAC) + world map
const SLOT_KEYS = ['dc_tycoon_slot_0', 'dc_tycoon_slot_1', 'dc_tycoon_slot_2'];
const AUTOSAVE_KEY = 'dc_tycoon_autosave';
const ACTIVE_SLOT_KEY = 'dc_tycoon_active_slot'; // '0' | '1' | '2' | 'autosave'
const SKIP_LOAD_KEY = 'dc_skip_load'; // sessionStorage

export interface SaveData {
  version: number;
  timestamp: number;
  money: number;
  reputation: number;
  gameTime: number;
  gameSpeed: number;
  locations: Record<string, LocationData>;
  upgrades: Upgrades;
  currentLocation: string;
  currentFloor: string;
  staff: StaffMember[];
  asnPurchased: boolean;
  piIpPurchased: boolean;
  activeEvents: ActiveEvent[];
  backboneLinks: BackboneLink[];
  availableContracts: AvailableContract[];
  activeContracts: ActiveContract[];
  lastContractRefreshDay: number;
  loans: Loan[];
  pendingDeliveries: PendingDelivery[];
}

interface SlottedSave extends SaveData {
  slotName: string;
}

export interface SlotMeta {
  slotIndex: number;
  name: string;
  timestamp: number;
  money: number;
  gameTime: number;
  reputation: number;
}

// ── Public helpers ────────────────────────────────────────────────────────────

function readSlot(key: string): SlottedSave | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw) as SlottedSave;
    if (data.version !== SAVE_VERSION) return null;
    return data;
  } catch {
    return null;
  }
}

export function getSlotMetas(): (SlotMeta | null)[] {
  return SLOT_KEYS.map((key, i) => {
    const s = readSlot(key);
    if (!s) return null;
    return { slotIndex: i, name: s.slotName || `Slot ${i + 1}`, timestamp: s.timestamp, money: s.money, gameTime: s.gameTime, reputation: s.reputation };
  });
}

export function getAutoSaveMeta(): SlotMeta | null {
  const s = readSlot(AUTOSAVE_KEY);
  if (!s) return null;
  return { slotIndex: -1, name: 'Auto-Save', timestamp: s.timestamp, money: s.money, gameTime: s.gameTime, reputation: s.reputation };
}

/** Called once during App startup — respects the Reset skip-load flag. */
export function tryLoadActiveSlot(): SaveData | null {
  try {
    if (sessionStorage.getItem(SKIP_LOAD_KEY)) {
      sessionStorage.removeItem(SKIP_LOAD_KEY);
      return null;
    }
    const active = localStorage.getItem(ACTIVE_SLOT_KEY);
    if (!active) return null;
    const key = active === 'autosave' ? AUTOSAVE_KEY : SLOT_KEYS[parseInt(active, 10)];
    if (!key) return null;
    return readSlot(key);
  } catch {
    return null;
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseSaveLoadProps {
  money: number;
  reputation: number;
  gameTime: number;
  gameSpeed: number;
  locations: Record<string, LocationData>;
  upgrades: Upgrades;
  staff: StaffMember[];
  currentLocation: string;
  currentFloor: string;
  asnPurchased: boolean;
  piIpPurchased: boolean;
  activeEvents: ActiveEvent[];
  backboneLinks: BackboneLink[];
  availableContracts: AvailableContract[];
  activeContracts: ActiveContract[];
  lastContractRefreshDay: number;
  loans: Loan[];
  pendingDeliveries: PendingDelivery[];
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  setReputation: React.Dispatch<React.SetStateAction<number>>;
  setGameTime: React.Dispatch<React.SetStateAction<number>>;
  setGameSpeed: React.Dispatch<React.SetStateAction<number>>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  setUpgrades: React.Dispatch<React.SetStateAction<Upgrades>>;
  setStaff: React.Dispatch<React.SetStateAction<StaffMember[]>>;
  setCurrentLocation: React.Dispatch<React.SetStateAction<string>>;
  setCurrentFloor: React.Dispatch<React.SetStateAction<string>>;
  setAsnPurchased: React.Dispatch<React.SetStateAction<boolean>>;
  setPiIpPurchased: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveEvents: React.Dispatch<React.SetStateAction<ActiveEvent[]>>;
  setBackboneLinks: React.Dispatch<React.SetStateAction<BackboneLink[]>>;
  setAvailableContracts: React.Dispatch<React.SetStateAction<AvailableContract[]>>;
  setActiveContracts: React.Dispatch<React.SetStateAction<ActiveContract[]>>;
  setLastContractRefreshDay: React.Dispatch<React.SetStateAction<number>>;
  setLoans: React.Dispatch<React.SetStateAction<Loan[]>>;
  setPendingDeliveries: React.Dispatch<React.SetStateAction<PendingDelivery[]>>;
  triggerNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

export const useSaveLoad = ({
  money, reputation, gameTime, gameSpeed,
  locations, upgrades, staff,
  currentLocation, currentFloor, asnPurchased, piIpPurchased, activeEvents,
  backboneLinks, availableContracts, activeContracts, lastContractRefreshDay,
  loans, pendingDeliveries,
  setMoney, setReputation, setGameTime, setGameSpeed,
  setLocations, setUpgrades, setStaff,
  setCurrentLocation, setCurrentFloor, setAsnPurchased, setPiIpPurchased, setActiveEvents,
  setBackboneLinks, setAvailableContracts, setActiveContracts, setLastContractRefreshDay,
  setLoans, setPendingDeliveries,
  triggerNotification,
}: UseSaveLoadProps) => {
  // Build the current game state snapshot
  const buildSaveData = useCallback((): SaveData => ({
    version: SAVE_VERSION,
    timestamp: Date.now(),
    money, reputation, gameTime, gameSpeed,
    locations, upgrades, staff,
    currentLocation, currentFloor,
    asnPurchased, piIpPurchased,
    activeEvents,
    backboneLinks,
    availableContracts,
    activeContracts,
    lastContractRefreshDay,
    loans,
    pendingDeliveries,
  }), [money, reputation, gameTime, gameSpeed, locations, upgrades, staff, currentLocation, currentFloor, asnPurchased, piIpPurchased, activeEvents, backboneLinks, availableContracts, activeContracts, lastContractRefreshDay, loans, pendingDeliveries]);

  const buildRef = useRef(buildSaveData);
  useEffect(() => { buildRef.current = buildSaveData; }, [buildSaveData]);

  // ── Save to a numbered slot (0-2) ──────────────────────────────────────────
  const saveToSlot = useCallback((slotIndex: number, name: string): void => {
    const data: SlottedSave = { ...buildRef.current(), slotName: name.trim() || `Slot ${slotIndex + 1}` };
    localStorage.setItem(SLOT_KEYS[slotIndex], JSON.stringify(data));
    localStorage.setItem(ACTIVE_SLOT_KEY, String(slotIndex));
    triggerNotification(`Saved to "${data.slotName}"`, 'success');
  }, [triggerNotification]);

  // ── Load from a slot ───────────────────────────────────────────────────────
  const loadFromSlot = useCallback((slotIndex: number): boolean => {
    const key = slotIndex === -1 ? AUTOSAVE_KEY : SLOT_KEYS[slotIndex];
    const data = readSlot(key);
    if (!data) { triggerNotification('Save slot is empty or incompatible.', 'error'); return false; }
    setMoney(data.money);
    setReputation(data.reputation);
    setGameTime(data.gameTime);
    setGameSpeed(data.gameSpeed);
    setLocations(data.locations);
    setUpgrades(data.upgrades);
    setStaff(data.staff ?? []);
    setCurrentLocation(data.currentLocation);
    setCurrentFloor(data.currentFloor);
    setAsnPurchased(data.asnPurchased ?? false);
    setPiIpPurchased(data.piIpPurchased ?? false);
    setActiveEvents(data.activeEvents ?? []);
    setBackboneLinks(data.backboneLinks ?? []);
    setAvailableContracts(data.availableContracts ?? []);
    setActiveContracts(data.activeContracts ?? []);
    setLastContractRefreshDay(data.lastContractRefreshDay ?? 0);
    setLoans(data.loans ?? []);
    setPendingDeliveries(data.pendingDeliveries ?? []);
    localStorage.setItem(ACTIVE_SLOT_KEY, slotIndex === -1 ? 'autosave' : String(slotIndex));
    triggerNotification(`Loaded "${(data as SlottedSave).slotName || 'Auto-Save'}"`, 'success');
    return true;
  }, [setMoney, setReputation, setGameTime, setGameSpeed, setLocations, setUpgrades, setStaff, setCurrentLocation, setCurrentFloor, setAsnPurchased, setPiIpPurchased, setActiveEvents, setBackboneLinks, setAvailableContracts, setActiveContracts, setLastContractRefreshDay, setLoans, setPendingDeliveries, triggerNotification]);

  // ── Delete a slot ──────────────────────────────────────────────────────────
  const deleteSlot = useCallback((slotIndex: number): void => {
    if (slotIndex < 0 || slotIndex > 2) return;
    localStorage.removeItem(SLOT_KEYS[slotIndex]);
    // If this was the active slot, clear active pointer
    if (localStorage.getItem(ACTIVE_SLOT_KEY) === String(slotIndex)) {
      localStorage.removeItem(ACTIVE_SLOT_KEY);
    }
  }, []);

  // ── Auto-save every 60 s to the autosave key ───────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const data: SlottedSave = { ...buildRef.current(), slotName: 'Auto-Save' };
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(data));
    }, 60000);
    return () => clearInterval(interval);
  }, []); // stable — ref keeps state current

  return { saveToSlot, loadFromSlot, deleteSlot };
};
